import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useOnboarding } from "./OnboardingContext";

const ATTR = "data-onboarding";

const STEP_TARGETS: Record<number, string> = {
	0: "add-product",
	2: "create-item",
	3: "export-order",
	4: "invite-member",
};

const CSS = `
.onboarding-pulse {
  outline: 2px solid hsl(var(--primary));
  outline-offset: 2px;
  border-radius: var(--radius);
  animation: ob-ring 1.5s ease-in-out infinite;
}
@keyframes ob-ring {
  0%, 100% { outline-color: hsl(var(--primary) / 0.5); }
  50% { outline-color: hsl(var(--primary) / 1); }
}
`;

export function OnboardingHighlight() {
	const { isActive, currentStep, mode } = useOnboarding();
	const location = useLocation();
	const prevEls = useRef<Element[]>([]);

	useEffect(() => {
		// Clean previous
		prevEls.current.forEach((el) => el.classList.remove("onboarding-pulse"));
		prevEls.current = [];

		if (!isActive || mode === "chat") return;
		const target = STEP_TARGETS[currentStep];
		if (!target) return;

		// Query after a tick so the page has rendered
		const timer = setTimeout(() => {
			const els = Array.from(
				document.querySelectorAll(`[${ATTR}="${target}"]`),
			);
			els.forEach((el) => el.classList.add("onboarding-pulse"));
			prevEls.current = els;
		}, 100);

		// Also watch for late-mounting elements
		const observer = new MutationObserver(() => {
			const els = Array.from(
				document.querySelectorAll(`[${ATTR}="${target}"]`),
			);
			const newEls = els.filter(
				(el) => !el.classList.contains("onboarding-pulse"),
			);
			newEls.forEach((el) => el.classList.add("onboarding-pulse"));
			prevEls.current.push(...newEls);
		});
		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			clearTimeout(timer);
			observer.disconnect();
			prevEls.current.forEach((el) => el.classList.remove("onboarding-pulse"));
			prevEls.current = [];
		};
	}, [isActive, currentStep, mode, location.pathname]);

	if (!isActive || mode === "chat") return null;

	return <style>{CSS}</style>;
}
